import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import credentialApi from "../api/credentialApi";
import type { CredentialItemFormData } from "../types";

export const credentialKeys = {
  list: ["credentials"] as const,
  detail: (id: string) => ["credential", id] as const,
};

export function useCredentialList() {
  return useQuery({
    queryKey: credentialKeys.list,
    queryFn: credentialApi.list,
  });
}

export function useCredential(id: string) {
  return useQuery({
    queryKey: credentialKeys.detail(id),
    queryFn: () => credentialApi.get(id),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CredentialItemFormData) => credentialApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: credentialKeys.list }),
  });
}

export function useUpdateCredential(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CredentialItemFormData>) => credentialApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.list });
      qc.invalidateQueries({ queryKey: credentialKeys.detail(id) });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => credentialApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: credentialKeys.list }),
  });
}

export function useCredentialAlertCount(enabled = true) {
  return useQuery({
    queryKey: credentialKeys.list,
    queryFn: credentialApi.list,
    enabled,
    refetchOnWindowFocus: false,
    select: (items) =>
      items.filter((i) => i.rotation_status === "overdue" || i.rotation_status === "due_soon").length,
  });
}

export function useMarkRotated() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => credentialApi.markRotated(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: credentialKeys.list }),
  });
}
